import { Spinner } from "@ui/components/spinner";
import { cn } from "@ui/lib";
import {
	BookmarkIcon,
	ChefHatIcon,
	CircleAlertIcon,
	HeadsetIcon,
	PackageIcon,
	RepeatIcon,
	SearchIcon,
	ShoppingBasketIcon,
	TruckIcon,
} from "lucide-react";
import { type Copy, fill } from "./copy";
import { useCopy } from "./copy-context";

/**
 * One visible agent step ("Searching for “milk”", then "8 matches"). NokNok
 * asked for the agent's tool calls to be shown on screen, so every call
 * renders, as a quiet status line: the results under it are the news.
 */

type Steps = Copy["steps"];
type StepKey = keyof {
	[K in keyof Steps as Steps[K] extends string ? K : never]: true;
};

/** Each tool's icon and its two lines (copy.ts), while running and once done. */
const LABELS: Record<
	string,
	{ icon: typeof SearchIcon; running: StepKey; done: StepKey }
> = {
	search_products: {
		icon: SearchIcon,
		running: "searching",
		done: "searching",
	},
	find_ingredients: {
		icon: ChefHatIcon,
		running: "recipe",
		done: "recipeReady",
	},
	get_product: {
		icon: PackageIcon,
		running: "product",
		done: "productChecked",
	},
	add_to_basket: {
		icon: ShoppingBasketIcon,
		running: "adding",
		done: "basketUpdated",
	},
	update_basket: {
		icon: ShoppingBasketIcon,
		running: "updating",
		done: "basketUpdated",
	},
	clear_basket: {
		icon: ShoppingBasketIcon,
		running: "emptying",
		done: "emptied",
	},
	get_basket: {
		icon: ShoppingBasketIcon,
		running: "checkingBasket",
		done: "basketChecked",
	},
	set_substitution: {
		icon: RepeatIcon,
		running: "substitution",
		done: "substitutionNoted",
	},
	get_order_status: {
		icon: TruckIcon,
		running: "order",
		done: "orderChecked",
	},
	escalate_to_human: { icon: HeadsetIcon, running: "team", done: "teamTold" },
	record_memory_consent: {
		icon: BookmarkIcon,
		running: "answer",
		done: "answerNoted",
	},
	remember: {
		icon: BookmarkIcon,
		running: "remembering",
		done: "remembered",
	},
	forget_memory: {
		icon: BookmarkIcon,
		running: "forgetting",
		done: "forgotten",
	},
};

/** A search query longer than this is the shopper's whole sentence, not worth repeating. */
const QUOTE_MAX = 28;

export function ToolStep({
	toolName,
	input,
	output,
	done,
	failed,
	tone = "light",
}: {
	toolName: string;
	input: unknown;
	/** The tool's result, for counts ("8 matches"). */
	output?: unknown;
	done: boolean;
	failed: boolean;
	/** "dark" on the live call's brand backdrop. */
	tone?: "light" | "dark";
}) {
	const steps = useCopy().steps;
	const label = LABELS[toolName] ?? {
		icon: PackageIcon,
		running: "working",
		done: "done",
	};
	const text = failed
		? steps.failed
		: (describe(steps, toolName, input, done ? output : undefined) ??
			steps[done ? label.done : label.running]);
	const settled = done || failed;

	return (
		<p
			className={cn(
				"flex min-w-0 items-center gap-1.5 text-[12.5px]",
				toneClass(tone, failed),
			)}
		>
			<StepIcon
				icon={failed ? CircleAlertIcon : label.icon}
				settled={settled}
				failed={failed}
				tone={tone}
			/>
			<span dir="auto" className={cn("truncate", !settled && "shimmer")}>
				{text}
			</span>
		</p>
	);
}

/** The line's colour: muted, or the error's on a failed step. */
function toneClass(tone: "light" | "dark", failed: boolean): string {
	if (tone === "dark") {
		return failed ? "text-white" : "text-white/70";
	}
	return failed ? "text-destructive" : "text-(--shop-muted)";
}

/** A spinner while the step runs, then its icon (the alert when it failed). */
function StepIcon({
	icon: Icon,
	settled,
	failed,
	tone,
}: {
	icon: typeof SearchIcon;
	settled: boolean;
	failed: boolean;
	tone: "light" | "dark";
}) {
	const accent =
		tone === "dark" ? "text-(--shop-accent)" : "text-(--shop-brand-text)";
	if (!settled) {
		return <Spinner className={cn("size-3.5 shrink-0", accent)} />;
	}
	return (
		<Icon
			className={cn("size-3.5 shrink-0", !failed && accent)}
			aria-hidden
		/>
	);
}

function describe(
	steps: Steps,
	toolName: string,
	input: unknown,
	output: unknown,
): string | null {
	const value = (input ?? {}) as Record<string, unknown>;
	const result = (output ?? null) as Record<string, unknown> | null;
	if (toolName === "search_products") {
		const list = Array.isArray(value["alsoSearch"])
			? value["alsoSearch"].length + 1
			: 0;
		// An empty query lists the day's deals (while the call streams in, the
		// query may simply not have arrived yet).
		const deals = result ? !value["query"] : value["query"] === "";
		const query =
			!list &&
			typeof value["query"] === "string" &&
			value["query"] !== "" &&
			value["query"].length <= QUOTE_MAX
				? `“${value["query"]}”`
				: "";
		if (!result) {
			return deals
				? steps.deals
				: list
					? fill(steps.searchingProducts, { count: list })
					: query
						? fill(steps.searchingFor, { query })
						: steps.searching;
		}
		const count = Array.isArray(result["products"])
			? result["products"].length
			: 0;
		if (deals && count === 0) {
			return steps.noDeals;
		}
		if (count === 0) {
			return query
				? fill(steps.noMatchesFor, { query })
				: steps.noMatches;
		}
		return steps.matches(count);
	}
	if (toolName === "find_ingredients" && result) {
		const matches = Array.isArray(result["matches"])
			? (result["matches"] as Array<{ best: unknown }>)
			: [];
		const found = matches.filter((m) => m.best).length;
		return found === matches.length
			? steps.allIngredients
			: fill(steps.someIngredients, { found, total: matches.length });
	}
	return null;
}
